import { OrganizationCreateInput } from '@/API';
import { createOrganization } from '@/client/organization';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import config from '@/config';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CloseIcon } from '../Icons/CommonIcons';
import { OrgContext } from './OrgContext';
import { DesktopDatePicker } from '@mui/x-date-pickers';

type Inputs = {
  image?: string;
  name: string;
  subdomain: string;
  numAdmin: number;
  numHost: number;
  status: string;
  domains: string;
  orgAdminFirstName: string;
  orgAdminLastName: string;
  orgAdminEmail: string;
  onFreeTrial: string;
  freeTrialExpiration: Date | string | null;
};

export default function CreateOrgModal() {
  // State
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Hooks
  const navigate = useNavigate();
  const { isEdit, setIsEdit, showModal, setShowModal, setQueryOrgs } = useContext(OrgContext);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    watch,
    setError,
    clearErrors,
    control,
    setValue
  } = useForm<Inputs>({});

  const watchDomains = watch('domains');
  const watchOrgAdminEmail = watch('orgAdminEmail');
  const watchOnFreeTrial = watch('onFreeTrial');

  useEffect(() => {
    if (watchDomains && watchOrgAdminEmail) {
      const domains = watchDomains.split(',').map((domain) => domain.trim());
      const emailDomain = watchOrgAdminEmail.split('@')[1];

      if (!domains.includes(emailDomain)) {
        setError('orgAdminEmail', {
          type: 'manual',
          message: 'Admin email must belong to one of the specified domains'
        });
      } else {
        clearErrors('orgAdminEmail');
      }
    }
  }, [watchDomains, watchOrgAdminEmail, setError, clearErrors]);

  const resetForm = () => {
    setShowModal(false);
    if (isEdit) {
      setIsEdit(false);
    }
    reset();
  };

  useEffect(() => {
    reset();
  }, [showModal]);

  const createOrg = async (org: OrganizationCreateInput) => {
    const response = await createOrganization(org);
    const tenantId = response?.tenant;
    navigate(`/dashboard/orgs/${tenantId}/users`);
    return response;
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const groupsCanRead = [`tenant:${data.subdomain}`];

    // Format the date if it's a Date object
    let freeTrialExpiration = null;
    if (data.onFreeTrial.toString() === 'true' && data.freeTrialExpiration) {
      if (data.freeTrialExpiration instanceof Date) {
        freeTrialExpiration = data.freeTrialExpiration.toISOString();
      } else {
        freeTrialExpiration = data.freeTrialExpiration;
      }
    }

    const orgApiData: OrganizationCreateInput = {
      name: data.name,
      adminLicenses: parseInt(data.numAdmin.toString(), 10),
      hostLicenses: parseInt(data.numHost.toString(), 10),
      organizationActive: data.status.toString() === 'true',
      subdomain: data.subdomain,
      emailDomains: data.domains.split(',').map((domain) => domain.trim()),
      groupsCanRead: groupsCanRead,
      onFreeTrial: data.onFreeTrial.toString() === 'true',
      freeTrialExpiration
    };

    setIsSubmitting(true);

    try {
      await createOrg(orgApiData);

      setQueryOrgs(true);
      resetForm();
    } catch (err: any) {
      const message = err?.errors?.[0]?.message || err?.message || 'Unable to create organization';
      if (/name already exists/i.test(message)) {
        setError('name', { message });
      } else if (/URL already exists/i.test(message)) {
        setError('subdomain', { message });
      } else {
        setError('name', { message });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={showModal} onClose={resetForm} maxWidth={'md'} fullWidth={true}>
      <DialogTitle>
        <div className="text-[24px] font-bold mb-[16px]">
          Create Organization
          <IconButton
            aria-label="close"
            onClick={resetForm}
            sx={{
              position: 'absolute',
              right: 28,
              top: 28
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="scrollContainer">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContentText className="text-black">Organization Name</DialogContentText>
          <TextField
            className="mb-[36px]"
            margin="dense"
            id="0"
            type="text"
            fullWidth
            autoFocus
            variant="outlined"
            placeholder="Enter a name for this organization"
            error={!!formState.errors.name}
            helperText={formState.errors.name?.message}
            {...register('name', {
              required: 'Organization name is required'
            })}
          />
          <div className="flex justify-between gap-4 mb-[36px]">
            <span className="w-[400px]">
              <DialogContentText className="text-black">Number of Admin Licenses</DialogContentText>
              <TextField
                className="mb-[8px]"
                margin="dense"
                id="1"
                type="number"
                fullWidth
                variant="outlined"
                placeholder="Enter a number"
                {...register('numAdmin', {
                  required: 'Number of licenses required',
                  min: { value: 1, message: 'Must be at least 1' },
                  validate: (value) => value >= 1 || 'Must be at least 1'
                })}
                error={!!formState.errors.numAdmin}
                helperText={formState.errors.numAdmin?.message}
              />
            </span>
            <span className="w-[400px]">
              <DialogContentText className="text-black">Number of Host Licenses</DialogContentText>
              <TextField
                className="mb-[8px]"
                margin="dense"
                id="2"
                type="number"
                fullWidth
                variant="outlined"
                placeholder="Enter a number"
                {...register('numHost', {
                  required: 'Number of licenses required',
                  min: { value: 1, message: 'Must be at least 1' },
                  validate: (value) => value >= 1 || 'Must be at least 1'
                })}
                error={!!formState.errors.numHost}
                helperText={formState.errors.numHost?.message}
              />
            </span>
          </div>
          <div className="flex justify-between gap-4 mb-[32px]">
            <FormControl className="w-[400px]">
              <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                Organization Status
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                defaultValue="true"
                className="h-56 my-8"
              >
                <FormControlLabel
                  value="true"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Active"
                  {...register('status')}
                />
                <FormControlLabel
                  value="false"
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Paused"
                  {...register('status')}
                />
              </RadioGroup>
            </FormControl>
            <span className="w-[400px]">
              <DialogContentText className="text-black">Organization URL</DialogContentText>
              <div className="flex">
                <TextField
                  className="w-[280px]"
                  margin="dense"
                  id="4"
                  type="text"
                  fullWidth
                  variant="outlined"
                  placeholder="Organization Name"
                  {...register('subdomain', {
                    required: 'Organization URL is required',
                    onChange: (e) => {
                      const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '');
                      setValue('subdomain', sanitized, { shouldValidate: true });
                    }
                  })}
                  error={!!formState.errors.subdomain}
                  helperText={formState.errors.subdomain?.message}
                />
                <div className="flex items-center ml-[6px]">
                  <span>.{config.siteDomain}</span>
                </div>
              </div>
            </span>
          </div>

          <div className="flex justify-between gap-4 mb-[36px]">
            <span className="w-[400px]">
              <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                Free Trial
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                defaultValue={'false'}
                className="h-56 my-8"
              >
                <FormControlLabel
                  value={'true'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="On"
                  {...register('onFreeTrial')}
                />
                <FormControlLabel
                  value={'false'}
                  control={
                    <Radio
                      sx={{
                        '&.Mui-checked': { color: '#F58C29' },
                        '& .MuiSvgIcon-root': {
                          fontSize: 32
                        }
                      }}
                    />
                  }
                  label="Off"
                  {...register('onFreeTrial')}
                />
              </RadioGroup>
            </span>
            {watchOnFreeTrial === 'true' && (
              <span className="w-[400px]">
                <DialogContentText className="text-black">Free Trial Expiration</DialogContentText>
                <Controller
                  name="freeTrialExpiration"
                  control={control}
                  rules={{
                    validate: (value) =>
                      (watchOnFreeTrial === 'true' && !!value) || 'Free trial expiration required'
                  }}
                  render={({ field }) => (
                    <DesktopDatePicker
                      {...field}
                      className="mb-[8px]"
                      slotProps={{
                        textField: {
                          margin: 'dense',
                          fullWidth: true,
                          variant: 'outlined',
                          error: !!formState.errors.freeTrialExpiration,
                          helperText: formState.errors.freeTrialExpiration?.message
                        }
                      }}
                    />
                  )}
                />
              </span>
            )}
          </div>
          <div className="mb-[36px]">
            <DialogContentText className="text-black">Available Email Domains</DialogContentText>
            <TextField
              margin="dense"
              id="5"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Enter email domains available to join this Organization (use , to separate)"
              {...register('domains', {
                required: 'At least one email domain is required',
                pattern: {
                  value: /^[^,\s]+(,[^,\s]+)*$/,
                  message: 'Must be a comma-separated list with one or more domains'
                }
              })}
              error={!!formState.errors.domains}
              helperText={formState.errors.domains?.message}
            />
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <button className="btn btn-neutral secondary mr-[5px]" onClick={resetForm}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
}
